import type { AxiosInstance } from 'axios';

import type { GameSessionResult, GameSessionStatus } from '@assets/enums/game-session.enum';

import type { GameSessionHole } from './game-sessions-api';
import type { PaginatedList, UUID } from './types';

export type PromoCodeMetadata = {
  id: string;
  code: string;
  display_name: string;
  nominal_amount: string;
  currency: string;
  total_activations: number;
  activated_count: number;
  consumed_count: number;
  remaining_activations: number;
  locked: boolean;
  locked_at: string | null;
  lock_reason: string | null;
  starts_at: string | null;
  expires_at: string | null;
  notes: string | null;
  created_by: { id: string; email: string } | null;
  created_at: string;
  updated_at: string;
};

export type PromoCode = PromoCodeMetadata & {
  games: number;
  wins: number;
  losses: number;
  paid_out: string;
  staked: string;
  coupon_staked: string;
  cash_staked: string;
  revenue: string;
  profit: string;
};

export type PromoCodeCreate = {
  code: string;
  display_name: string;
  nominal_amount: string;
  total_activations: number;
  currency?: string;
  starts_at?: string | null;
  expires_at?: string | null;
  notes?: string | null;
};

export type PromoCodeUpdate = Partial<Omit<PromoCodeCreate, 'code' | 'currency'>> & {
  locked?: boolean;
  lock_reason?: string | null;
};

export type PromoCodesListArgs = {
  page?: number;
  page_size?: number;
  search?: string;
  schedule?: 'scheduled' | 'active' | 'expired' | 'not_expired';
  locked?: boolean;
  created_by?: string;
  active_on?: string;
};

export type PromoCodeStats = {
  code: string;
  created_at: string;
  total_activations: number;
  activated_count: number;
  consumed_count: number;
  remaining_activations: number;
  currency: string;
  notional_amount: string;
  activated_amount: string;
  consumed_amount: string;
  timeline: { date: string; activated: number; consumed: number }[];
};

export type PromoGame = {
  session_id: string;
  activation_id: string;
  activated_at: string;
  phone: string;
  player_name: string | null;
  course: { id: string; name: string; partner: { id: string; legal_name: string } } | null;
  hole: GameSessionHole | null;
  challenge: string | null;
  challenge_display_name: string | null;
  status: GameSessionStatus;
  result: GameSessionResult | null;
  wager_amount: string;
  coupon_amount: string;
  cash_amount: string;
  final_payout: string;
  revenue: string;
  profit: string;
  started_at: string | null;
  completed_at: string | null;
  tee_box_device_id: UUID | null;
  hole_box_device_id: UUID | null;
};

export class PromoCodesApi {
  constructor(private instance: AxiosInstance) {}

  /** Fetches campaigns using only documented filters; the server orders newest first. */
  async getList(params: PromoCodesListArgs) {
    return this.instance
      .get<PaginatedList<PromoCode>>('/promo-codes/', { params })
      .then((res) => res.data);
  }

  /** Retrieves a campaign by its printed code, rather than its UUID. */
  async getByCode(code: string) {
    return this.instance
      .get<PromoCode>(`/promo-codes/${encodeURIComponent(code)}/`)
      .then((res) => res.data);
  }

  /** Loads daily activation and consumption counts for the complete campaign history. */
  async getStats(code: string) {
    return this.instance
      .get<PromoCodeStats>(`/promo-codes/${encodeURIComponent(code)}/stats/`)
      .then((res) => res.data);
  }

  /** Loads a page of game sessions associated with a campaign. */
  async getGames(code: string, page: number) {
    return this.instance
      .get<
        PaginatedList<PromoGame>
      >(`/promo-codes/${encodeURIComponent(code)}/games/`, { params: { page } })
      .then((res) => res.data);
  }

  /** Registers a campaign; the backend records the authenticated creator. */
  async create(dto: PromoCodeCreate) {
    return this.instance.post<PromoCodeMetadata>('/promo-codes/', dto).then((res) => res.data);
  }

  /** Updates metadata; locking stops new activations without invalidating issued tokens. */
  async update(code: string, dto: PromoCodeUpdate) {
    return this.instance
      .patch<PromoCodeMetadata>(`/promo-codes/${encodeURIComponent(code)}/`, dto)
      .then((res) => res.data);
  }
}
